<template>
    <v-card elevation="2">
        <v-row dense class="px-4 pt-4">
            <v-col cols="12" sm="6" md="2">
                <v-select v-model="preset" :items="INSPECTION_PRESETS" label="Reported" density="compact"
                          variant="outlined" hide-details/>
            </v-col>
            <template v-if="preset === 'custom'">
                <v-col cols="6" sm="3" md="2">
                    <v-text-field v-model="customFrom" type="date" label="From" density="compact" variant="outlined"
                                  hide-details :max="customTo || undefined"/>
                </v-col>
                <v-col cols="6" sm="3" md="2">
                    <v-text-field v-model="customTo" type="date" label="To" density="compact" variant="outlined"
                                  hide-details :min="customFrom || undefined" :error="customInvalid"/>
                </v-col>
            </template>
            <v-col cols="12" sm="6" md="3">
                <v-autocomplete v-model="vehicle" :items="vehicles" item-title="label" item-value="id" multiple chips
                                closable-chips label="Vehicle" density="compact" variant="outlined" hide-details
                                clearable :loading="vehiclesLoading" no-data-text="No vehicles"/>
            </v-col>
            <v-col cols="6" sm="3" md="2">
                <v-select v-model="severity" :items="SEVERITIES" label="Severity" density="compact"
                          variant="outlined" hide-details clearable/>
            </v-col>
            <v-col cols="6" sm="3" md="2">
                <v-select v-model="status" :items="BOARD_STATUSES" label="Status" density="compact"
                          variant="outlined" hide-details/>
            </v-col>
            <v-col v-if="compact" cols="12">
                <v-select v-model="phoneSort" :items="PHONE_SORTS" label="Sort" density="compact"
                          variant="outlined" hide-details/>
            </v-col>
        </v-row>

        <v-alert v-if="customInvalid" type="warning" variant="tonal" density="compact" class="mx-4 my-2">
            The end date is before the start date.
        </v-alert>

        <div v-if="activeFilters.length" class="d-flex flex-wrap align-center ga-2 px-4 pt-3">
            <v-chip v-for="c in activeFilters" :key="c.key" size="small" closable @click:close="c.clear()">
                {{ c.label }}
            </v-chip>
            <v-btn size="small" variant="text" color="primary" @click="clearAll">Clear all</v-btn>
        </div>

        <div v-if="selectedRows.length" class="d-flex flex-wrap align-center ga-2 px-4 pt-3">
            <span class="text-body-2">{{ selectedRows.length }} selected</span>
            <v-btn size="small" color="success" variant="flat" prepend-icon="mdi-check-all"
                   @click="openResolve(selectedRows)">Resolve selected</v-btn>
            <v-btn size="small" variant="text" @click="selected = []">Clear selection</v-btn>
        </div>

        <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mx-4 my-2">
            {{ loadError }}
            <template #append>
                <v-btn size="small" variant="text" @click="fetchRows">Retry</v-btn>
            </template>
        </v-alert>

        <template v-if="compact">
            <v-progress-linear :active="loading" indeterminate color="primary" height="2" class="mt-2"/>
            <v-list v-if="rows.length" class="py-0" lines="three">
                <template v-for="d in rows" :key="d.id">
                    <v-list-item>
                        <v-list-item-title class="d-flex align-center ga-2">
                            <span class="text-truncate">{{ d.label }}</span>
                            <v-spacer/>
                            <v-chip size="x-small" variant="flat" :color="sevColor(d.severity)">{{ sevLabel(d.severity) }}</v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle class="mt-1">
                            <div>
                                {{ d.asset?.label || '-' }} &middot; {{ fmtDate(d.created_at, zone) }}
                                <span v-if="reporterName(d)"> &middot; {{ reporterName(d) }}</span>
                            </div>
                            <div v-if="d.note" class="text-truncate">{{ d.note }}</div>
                            <div v-if="d.status === 'resolved'">{{ resolvedLine(d) }}</div>
                        </v-list-item-subtitle>
                        <div class="d-flex flex-wrap align-center ga-1 mt-1">
                            <v-chip size="x-small" variant="tonal" :color="defectStatusColor(d.status)">
                                {{ defectStatusLabel(d.status) }}
                            </v-chip>
                            <span v-if="d.status !== 'resolved'" class="text-caption"
                                  :class="tooLong(d) ? 'text-error font-weight-bold' : 'text-medium-emphasis'">
                                Open {{ openFor(d.created_at) }}
                            </span>
                            <v-chip v-if="d.service_task" size="x-small" variant="outlined" :to="workOrderRoute(d)">
                                {{ woLabel(d) }}
                            </v-chip>
                            <v-spacer/>
                            <v-btn v-if="d.photo_path" size="x-small" variant="text" icon="mdi-image"
                                   aria-label="View photo" @click="emit('photo', d.photo_path)"/>
                            <v-btn v-if="d.inspection_id" size="x-small" variant="text" icon="mdi-clipboard-text-outline"
                                   aria-label="View inspection" @click="emit('open-inspection', d.inspection_id)"/>
                            <v-btn v-if="!d.service_task_id && d.status !== 'resolved'" size="x-small" variant="tonal"
                                   color="primary" :loading="busy === d.id + ':wo'" :disabled="!!busy"
                                   @click="createWorkOrder(d)">Work order</v-btn>
                            <v-btn v-if="d.status !== 'resolved'" size="x-small" variant="text" color="success"
                                   :disabled="!!busy" @click="openResolve([d])">Resolve</v-btn>
                        </div>
                    </v-list-item>
                    <v-divider/>
                </template>
            </v-list>
            <div v-if="lastPage > 1" class="d-flex justify-center py-3">
                <v-pagination v-model="page" :length="lastPage" :total-visible="4" density="compact"/>
            </div>
            <div v-if="!rows.length && !loading && !loadError" class="pa-6 text-center text-medium-emphasis">
                <v-icon v-if="!activeFilters.length" size="40" color="success" class="mb-2">mdi-check-circle-outline</v-icon>
                <div>{{ emptyText }}</div>
                <v-btn v-if="activeFilters.length" class="mt-2" size="small" variant="tonal" @click="clearAll">
                    Clear filters
                </v-btn>
            </div>
        </template>

        <v-data-table-server v-else v-model="selected" v-model:page="page" v-model:items-per-page="perPage"
                             v-model:sort-by="sortBy" must-sort show-select item-value="id"
                             item-selectable="selectable"
                             :headers="headers" :items="tableRows" :items-length="totalItems"
                             :loading="loading" :items-per-page-options="[10, 20, 50, 100]" density="compact">
            <template #item.created_at="{item}">
                <span class="text-no-wrap">{{ fmtDate(item.created_at, zone) }}</span>
            </template>
            <template #item.vehicle="{item}">{{ item.asset?.label || '-' }}</template>
            <template #item.label="{item}">
                {{ item.label }}
                <v-btn v-if="item.photo_path" size="x-small" variant="text" icon="mdi-image"
                       aria-label="View photo" @click="emit('photo', item.photo_path)"/>
                <div v-if="item.note" class="text-caption text-medium-emphasis">{{ item.note }}</div>
                <a v-if="item.inspection_id" href="#" class="text-caption text-primary"
                   @click.prevent="emit('open-inspection', item.inspection_id)">View inspection</a>
            </template>
            <template #item.severity="{item}">
                <v-chip size="x-small" variant="flat" :color="sevColor(item.severity)">{{ sevLabel(item.severity) }}</v-chip>
            </template>
            <template #item.status="{item}">
                <div class="py-1">
                    <v-chip size="x-small" variant="tonal" :color="defectStatusColor(item.status)">
                        {{ defectStatusLabel(item.status) }}
                    </v-chip>
                    <div v-if="item.service_task" class="mt-1">
                        <v-chip size="x-small" variant="outlined" prepend-icon="mdi-wrench" :to="workOrderRoute(item)">
                            {{ woLabel(item) }}
                        </v-chip>
                    </div>
                    <div v-if="item.status === 'resolved'" class="text-caption text-medium-emphasis mt-1">
                        {{ resolvedLine(item) }}
                        <div v-if="item.resolution_note" class="font-italic">{{ item.resolution_note }}</div>
                    </div>
                </div>
            </template>
            <template #item.open_for="{item}">
                <span v-if="item.status === 'resolved'" class="text-medium-emphasis">-</span>
                <span v-else class="text-no-wrap" :class="{'text-error font-weight-bold': tooLong(item)}">
                    {{ openFor(item.created_at) }}
                </span>
            </template>
            <template #item.reporter="{item}">{{ reporterName(item) || '-' }}</template>
            <template #item.actions="{item}">
                <span class="text-no-wrap">
                    <v-btn v-if="!item.service_task_id && item.status !== 'resolved'" size="x-small" variant="tonal"
                           color="primary" :loading="busy === item.id + ':wo'" :disabled="!!busy"
                           @click="createWorkOrder(item)">Work order</v-btn>
                    <v-btn v-if="item.status !== 'resolved'" size="x-small" variant="text" color="success"
                           :disabled="!!busy" @click="openResolve([item])">Resolve</v-btn>
                </span>
            </template>
            <template #no-data>
                <div class="pa-6 text-center text-medium-emphasis">
                    <v-icon v-if="!activeFilters.length" size="40" color="success" class="mb-2">mdi-check-circle-outline</v-icon>
                    <div>{{ emptyText }}</div>
                    <v-btn v-if="activeFilters.length" class="mt-2" size="small" variant="tonal" @click="clearAll">
                        Clear filters
                    </v-btn>
                </div>
            </template>
        </v-data-table-server>

        <ResolveDefectDialog v-model="resolveOpen" :defects="resolving" @resolved="onResolved"/>
    </v-card>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useDisplay} from "vuetify";
import {send} from "@/composables/apiClient";
import {companyTimezone} from "@/composables/useTimezone";
import {apiError, presetRange, rangePhrase} from "@/components/drivers/shifts/shiftFormat";
import ResolveDefectDialog from "./ResolveDefectDialog.vue";
import {createDefectWorkOrder, workOrderRoute, workOrderStatusLabel} from "./defectActions";
import {
    BOARD_STATUSES, defectStatusColor, defectStatusLabel, driverName, fmtDate, ids, INSPECTION_PRESETS, one,
    OPEN_TOO_LONG_DAYS, openDays, openFor, pageOf, patchQuery, pick, pickPreset, resolutionLabel, sameQuery,
    SEVERITIES, sevColor, sevLabel, titleOf, userName, vehicleOptions, ymdOr,
} from "./inspectionFormat";

const emit = defineEmits(["photo", "changed", "open-inspection"]);

const route = useRoute();
const router = useRouter();
const {xs} = useDisplay();
const compact = computed(() => xs.value);

const SORTS = ["created_at", "severity", "status", "vehicle"];
const headers = [
    {title: "Reported", key: "created_at"},
    {title: "Vehicle", key: "vehicle"},
    {title: "Defect", key: "label", sortable: false},
    {title: "Severity", key: "severity", align: "center"},
    {title: "Status", key: "status"},
    {title: "Open for", key: "open_for", align: "center", sortable: false},
    {title: "Reported by", key: "reporter", sortable: false},
    {title: "Actions", key: "actions", align: "center", sortable: false},
];
const PHONE_SORTS = [
    {value: "created_at:desc", title: "Newest first"},
    {value: "created_at:asc", title: "Oldest first"},
    {value: "severity:desc", title: "Most severe first"},
    {value: "status:asc", title: "Open first"},
    {value: "vehicle:asc", title: "Vehicle"},
];
// Its keys in the URL, apart from the inspection list's.
const P = "d_";

const preset = ref("all");
const customFrom = ref("");
const customTo = ref("");
const vehicle = ref([]);
const severity = ref(null);
const status = ref("open_all");
const sortBy = ref([{key: "created_at", order: "desc"}]);
const page = ref(1);
const perPage = ref(20);

const sortKey = computed(() => sortBy.value?.[0]?.key || "created_at");
const sortDir = computed(() => (sortBy.value?.[0]?.order === "asc" ? "asc" : "desc"));
const phoneSort = computed({
    get: () => `${sortKey.value}:${sortDir.value}`,
    set: (v) => {
        const [key, order] = v.split(":");
        sortBy.value = [{key, order}];
    },
});

let applying = false;
const applyQuery = (q) => {
    applying = true;
    preset.value = pickPreset(q[P + "range"], "all");
    customFrom.value = ymdOr(q[P + "from"]);
    customTo.value = ymdOr(q[P + "to"]);
    vehicle.value = ids(q[P + "vehicle"]);
    severity.value = pick(q[P + "severity"], SEVERITIES.map((s) => s.value), null);
    status.value = pick(q[P + "status"], BOARD_STATUSES.map((s) => s.value), "open_all");
    sortBy.value = [{key: pick(q[P + "sort"], SORTS, "created_at"), order: one(q[P + "dir"]) === "asc" ? "asc" : "desc"}];
    page.value = pageOf(q[P + "page"]);
    perPage.value = [10, 20, 50, 100].includes(Number(one(q[P + "per_page"]))) ? Number(one(q[P + "per_page"])) : 20;
    nextTick(() => {
        applying = false;
    });
};

const myQuery = () => ({
    [P + "range"]: preset.value === "all" ? "" : preset.value,
    [P + "from"]: preset.value === "custom" ? customFrom.value : "",
    [P + "to"]: preset.value === "custom" ? customTo.value : "",
    [P + "vehicle"]: vehicle.value,
    [P + "severity"]: severity.value || "",
    [P + "status"]: status.value === "open_all" ? "" : status.value,
    [P + "sort"]: sortKey.value === "created_at" ? "" : sortKey.value,
    [P + "dir"]: sortDir.value === "desc" ? "" : "asc",
    [P + "page"]: page.value > 1 ? page.value : "",
    [P + "per_page"]: perPage.value === 20 ? "" : perPage.value,
});

const zone = ref(null);
const tz = computed(() => zone.value || companyTimezone());
const range = computed(() => {
    if (preset.value === "custom") return {from: customFrom.value, to: customTo.value};
    return presetRange(preset.value, tz.value) ?? {from: "", to: ""};
});
const customInvalid = computed(() =>
    preset.value === "custom" && !!customFrom.value && !!customTo.value && customFrom.value > customTo.value);

const vehicles = ref([]);
const vehiclesLoading = ref(false);
const vehicleLabel = (id) => vehicles.value.find((v) => v.id === id)?.label || `Vehicle ${id}`;

const activeFilters = computed(() => {
    const out = [];
    if (preset.value !== "all") {
        const when = rangePhrase(preset.value, range.value.from, range.value.to);
        out.push({key: "range", label: when || titleOf(INSPECTION_PRESETS, preset.value), clear: () => (preset.value = "all")});
    }
    vehicle.value.forEach((id) => out.push({
        key: `v${id}`, label: vehicleLabel(id), clear: () => (vehicle.value = vehicle.value.filter((x) => x !== id)),
    }));
    if (severity.value) out.push({key: "sev", label: titleOf(SEVERITIES, severity.value), clear: () => (severity.value = null)});
    if (status.value !== "open_all") {
        out.push({key: "st", label: titleOf(BOARD_STATUSES, status.value), clear: () => (status.value = "open_all")});
    }
    return out;
});

const emptyText = computed(() => {
    if (loading.value) return "Loading defects...";
    if (!activeFilters.value.length) return "No open defects. Defects reported by drivers show here.";
    return `No defects match ${activeFilters.value.map((c) => c.label).join(", ")}.`;
});

const clearAll = () => {
    preset.value = "all";
    customFrom.value = "";
    customTo.value = "";
    vehicle.value = [];
    severity.value = null;
    status.value = "open_all";
};

const filterParams = () => {
    const p = {status: status.value, sort: sortKey.value, dir: sortDir.value};
    if (range.value.from) p.from = range.value.from;
    if (range.value.to) p.to = range.value.to;
    if (vehicle.value.length) p.vehicle = vehicle.value;
    if (severity.value) p.severity = severity.value;
    return p;
};

const rows = ref([]);
const loading = ref(false);
const loadError = ref("");
const lastPage = ref(1);
const totalItems = ref(0);
// One action at a time, keyed "<id>:<action>" so only that button spins.
const busy = ref(null);

// Only unresolved defects can be picked for "Resolve selected".
const tableRows = computed(() => rows.value.map((d) => ({...d, selectable: d.status !== "resolved"})));
const selected = ref([]);
const selectedRows = computed(() => rows.value.filter((d) => d.status !== "resolved" && selected.value.includes(d.id)));

const reporterName = (d) => {
    const n = driverName(d.reporter);
    return n === "-" ? "" : n;
};
const tooLong = (d) => d.status !== "resolved" && (openDays(d.created_at) ?? 0) > OPEN_TOO_LONG_DAYS;
const woLabel = (d) => {
    const st = workOrderStatusLabel(d.service_task?.status);
    return `WO: ${d.service_task?.label || d.label}${st ? ` (${st})` : ""}`;
};
const resolvedLine = (d) => {
    const how = resolutionLabel(d.resolution);
    const who = userName(d.resolver);
    const when = fmtDate(d.resolved_at, zone.value);
    return [how, who || when ? `Resolved${who ? ` by ${who}` : ""}${when ? `, ${when}` : ""}` : ""]
        .filter(Boolean).join(" · ");
};

let requestSeq = 0;
const fetchRows = () => {
    const seq = ++requestSeq;
    if (customInvalid.value) {
        rows.value = [];
        totalItems.value = 0;
        lastPage.value = 1;
        loading.value = false;
        return;
    }
    loading.value = true;
    loadError.value = "";
    send("get", "fleet/inspection-defects", {...filterParams(), page: page.value, per_page: perPage.value})
        .then((r) => {
            if (seq !== requestSeq) return;
            const d = r.data.data ?? {};
            rows.value = d.data ?? [];
            lastPage.value = d.last_page ?? 1;
            totalItems.value = d.total ?? 0;
            zone.value = d.company_timezone ?? null;
            selected.value = selected.value.filter((id) => rows.value.some((x) => x.id === id && x.status !== "resolved"));
            if (!rows.value.length && page.value > 1) page.value = Math.max(1, lastPage.value);
        })
        .catch((e) => {
            if (seq !== requestSeq) return;
            loadError.value = apiError(e, "Defects could not be loaded.");
        })
        .finally(() => {
            if (seq === requestSeq) loading.value = false;
        });
};

let scheduled = false;
let writing = 0;
const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    nextTick(() => {
        scheduled = false;
        writing++;
        patchQuery(router, myQuery()).finally(() => writing--);
        fetchRows();
    });
};

const fetchVehicles = () => {
    vehiclesLoading.value = true;
    send("get", "assets/list", {include_deleted: 1})
        .then((r) => {
            vehicles.value = vehicleOptions(r?.data?.data);
        })
        .catch(() => {
            vehicles.value = [];
        })
        .finally(() => {
            vehiclesLoading.value = false;
        });
};

const createWorkOrder = async (d) => {
    busy.value = d.id + ":wo";
    try {
        if (await createDefectWorkOrder(d)) emit("changed");
    } finally {
        busy.value = null;
    }
};

const resolveOpen = ref(false);
const resolving = ref([]);
const openResolve = (list) => {
    resolving.value = list.slice();
    resolveOpen.value = true;
};
const onResolved = (doneIds) => {
    selected.value = selected.value.filter((id) => !doneIds.includes(id));
    emit("changed");
};

const filtersKey = computed(() => JSON.stringify([
    preset.value, customFrom.value, customTo.value, vehicle.value, severity.value, status.value,
    sortKey.value, sortDir.value, perPage.value,
]));
watch(filtersKey, () => {
    if (!applying) page.value = 1;
    selected.value = [];
    schedule();
});
watch(page, schedule);
watch(tz, (now, before) => {
    if (preset.value === "custom" || preset.value === "all") return;
    const a = presetRange(preset.value, now);
    const b = presetRange(preset.value, before);
    if (a?.from !== b?.from || a?.to !== b?.to) schedule();
});
watch(() => route.query, (q) => {
    if (!writing && !sameQuery(q, myQuery())) applyQuery(q);
});

applyQuery(route.query);
onMounted(() => {
    schedule();
    fetchVehicles();
});

defineExpose({refresh: fetchRows});
</script>
